/**
 * Generation speed run history — client-side p50/p95 instrumentation.
 *
 * Every completed generation records its wall-clock timing here
 * (localStorage-persisted, capped). Percentiles are partitioned by run
 * source: mixing a 5s mock simulation, a 0.8s channel-1 retrieval and a
 * 30s real app generation into one distribution would make the numbers
 * meaningless. Only measured values are stored — no estimates.
 */

export type GenerationRunSource =
  | 'channel-1'
  | 'channel-2-mock'
  | 'channel-2-real'
  | 'app-generation'

export interface GenerationRunRecord {
  source: GenerationRunSource
  totalMs: number
  recordedAt: number
  /** Honest per-stage measurements when the pipeline reported them
   * (e.g. lookup/bind for channel 1). Absent ≠ zero. */
  stages?: Record<string, number>
}

export interface GenerationSpeedStats {
  source: GenerationRunSource
  count: number
  p50Ms: number
  p95Ms: number
}

const STORAGE_KEY = 'cogo-studio:generation-runs:v1'
/** Cap on stored runs (oldest dropped first). */
const MAX_RUNS = 400

function readStorage(): GenerationRunRecord[] {
  try {
    if (typeof localStorage === 'undefined') return []
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) return []
    return parsed.filter(
      (r): r is GenerationRunRecord =>
        typeof r === 'object' && r !== null &&
        typeof (r as GenerationRunRecord).totalMs === 'number' &&
        typeof (r as GenerationRunRecord).source === 'string',
    )
  } catch {
    return []
  }
}

function writeStorage(runs: GenerationRunRecord[]): void {
  try {
    if (typeof localStorage === 'undefined') return
    localStorage.setItem(STORAGE_KEY, JSON.stringify(runs))
  } catch {
    // Quota/serialization failures must never break the dispatch path.
  }
}

/** Nearest-rank percentile over an unsorted sample. */
export function percentile(values: number[], p: number): number {
  if (values.length === 0) return 0
  const sorted = [...values].sort((a, b) => a - b)
  const rank = Math.max(1, Math.ceil((p / 100) * sorted.length))
  return sorted[rank - 1]
}

/** Read all stored runs (most recent last). */
export function readGenerationRuns(): GenerationRunRecord[] {
  return readStorage()
}

/** Compute p50/p95 for one source. Returns null when no runs exist. */
export function getGenerationSpeedStats(
  source: GenerationRunSource,
): GenerationSpeedStats | null {
  const totals = readStorage()
    .filter((r) => r.source === source)
    .map((r) => r.totalMs)
  if (totals.length === 0) return null
  return {
    source,
    count: totals.length,
    p50Ms: percentile(totals, 50),
    p95Ms: percentile(totals, 95),
  }
}

/**
 * Record a completed run and return the updated stats for its source —
 * the dispatch path embeds these into the result chat message so the
 * displayed percentiles are a point-in-time snapshot including this run.
 */
export function recordGenerationRun(
  run: Omit<GenerationRunRecord, 'recordedAt'> & { recordedAt?: number },
): GenerationSpeedStats {
  const record: GenerationRunRecord = {
    ...run,
    recordedAt: run.recordedAt ?? Date.now(),
  }
  const runs = [...readStorage(), record].slice(-MAX_RUNS)
  writeStorage(runs)
  const totals = runs.filter((r) => r.source === record.source).map((r) => r.totalMs)
  return {
    source: record.source,
    count: totals.length,
    p50Ms: percentile(totals, 50),
    p95Ms: percentile(totals, 95),
  }
}

/** Test/diagnostics helper — wipes the stored history. */
export function clearGenerationRuns(): void {
  try {
    if (typeof localStorage === 'undefined') return
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    // ignore
  }
}
